/**
 * Sprint 23.5 — Composition chat runtime.
 *
 * Wraps Sprint 22's `useV3ChatRuntime` for the Hire wizard. The wizard chat
 * sends scope `{kind: "composition", target_type: "agent"}` and a
 * `composition_draft` body param so the worker prompt always sees the
 * freshest field state — including user pencil edits between turns.
 *
 * It also watches the streamed message list for `update_composition_draft`
 * tool calls and forwards each one to a callback that patches local draft
 * state. The worker-side handler is a shape-validator + ack (no persistence),
 * so the streamed tool result already returns successfully; nothing extra is
 * needed to keep the conversation flowing.
 */

import { useEffect, useMemo, useRef } from "react";
import { useChat, type UIMessage } from "@ai-sdk/react";
import { AssistantChatTransport, useAISDKRuntime } from "@assistant-ui/react-ai-sdk";
import type { AssistantRuntime } from "@assistant-ui/react";
import { getAccessToken } from "../../lib/auth";
import type { HireDraft } from "../components/hire-wizard/types";

const env = import.meta.env as { VITE_MCP_ENDPOINT?: string };
const aiEndpoint = (env.VITE_MCP_ENDPOINT ?? "https://dev.kuvio.ai/mcp").replace(/\/mcp\/?$/, "") + "/ai/chat";

export interface CompositionChatOptions {
  /** Stable per-session id; sent to the proxy so usage logs share an id. */
  conversationId: string;
  /** Workspace id forwarded to the worker. */
  workspaceId?: string;
  /** Live draft, snapshotted into every chat-request body param. */
  draft: HireDraft;
  /** Fires once per intercepted `update_composition_draft` tool call. */
  onDraftPatch: (field: string, value: unknown) => void;
  /** Optional pre-seed for the message thread (e.g. wizard greeting). */
  seedMessages?: UIMessage[];
}

export interface CompositionChatResult {
  runtime: AssistantRuntime;
  chatMessages: UIMessage[];
  setChatMessages: (
    messages: UIMessage[] | ((messages: UIMessage[]) => UIMessage[]),
  ) => void;
  status: "submitted" | "streaming" | "ready" | "error";
  error: Error | undefined;
}

interface ToolPart {
  type: string;
  toolName?: string;
  toolCallId?: string;
  state?: string;
  input?: unknown;
  args?: unknown;
}

/**
 * Walk the assistant message parts and surface every NEW
 * `update_composition_draft` tool call whose input has finished streaming.
 *
 * Streaming phases (AI SDK v6 ToolUIPart.state):
 *  - "input-streaming"   → partial input, skip (we'd commit a half-typed field)
 *  - "input-available"   → full input parsed, tool about to run — APPLY HERE
 *  - "output-available"  → tool returned, input still readable — also APPLY (idempotent via seenCallIds)
 *  - "output-error"      → server rejected; do not apply
 *
 * Part `type` is either the named form `"tool-update_composition_draft"` or
 * the generic `"tool-call"` / `"dynamic-tool"` shapes with `toolName` set.
 */
function extractDraftPatches(
  messages: UIMessage[],
  seenCallIds: Set<string>,
): Array<{ field: string; value: unknown; id: string }> {
  const out: Array<{ field: string; value: unknown; id: string }> = [];
  for (const m of messages) {
    if (m.role !== "assistant" || !m.parts) continue;
    for (const part of m.parts as ToolPart[]) {
      const type = part.type;
      if (typeof type !== "string") continue;
      const isMatch =
        type === "tool-update_composition_draft" ||
        (type.startsWith("tool") && part.toolName === "update_composition_draft") ||
        (type === "dynamic-tool" && part.toolName === "update_composition_draft");
      if (!isMatch) continue;

      // Only apply once the input is fully parsed. Partial-streaming inputs
      // would commit a half-typed field name and then be marked as seen
      // forever, so the final value would be ignored.
      const state = part.state;
      if (state && state !== "input-available" && state !== "output-available") {
        continue;
      }

      const callId = part.toolCallId ?? `${m.id ?? "msg"}:${type}`;
      if (seenCallIds.has(callId)) continue;
      const raw = (part.input ?? part.args) as Record<string, unknown> | undefined;
      if (!raw || typeof raw.field !== "string") continue;
      out.push({ field: raw.field, value: raw.value, id: callId });
    }
  }
  return out;
}

export function useCompositionChatRuntime({
  conversationId,
  workspaceId,
  draft,
  onDraftPatch,
  seedMessages,
}: CompositionChatOptions): CompositionChatResult {
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const transportRef = useRef<AssistantChatTransport<UIMessage>>(null!);

  const draftKey = JSON.stringify(draft);

  const latestTransport = useMemo(
    () =>
      new AssistantChatTransport({
        api: aiEndpoint,
        headers: (): Record<string, string> => {
          const token = getAccessToken();
          return {
            "x-kuvio-host": "in-app-chat",
            ...(token ? { authorization: `Bearer ${token}` } : {}),
          };
        },
        body: {
          conversation_id: conversationId,
          ...(workspaceId ? { workspace_id: workspaceId } : {}),
          scope: { kind: "composition", target_type: "agent" },
          // Snapshot the draft on every request build. Pencil edits between
          // turns ride along with the next user message.
          composition_draft: draftRef.current,
        },
      }),
    // draftKey is intentional — it tells useMemo to rebuild the transport
    // when the draft changes, so subsequent sends carry the fresh snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversationId, workspaceId, draftKey],
  );

  if (transportRef.current === null) {
    transportRef.current = latestTransport;
  }

  useEffect(() => {
    transportRef.current = latestTransport;
  });

  const stableTransport = useMemo(
    () =>
      new Proxy(latestTransport, {
        get(_, prop) {
          const target = transportRef.current;
          const val = target[prop as keyof typeof target];
          return typeof val === "function" ? (val as Function).bind(target) : val;
        },
      }),
    // Stable for the lifetime of the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const chat = useChat({ transport: stableTransport, ...(seedMessages ? { messages: seedMessages } : {}) });
  const runtime = useAISDKRuntime(chat);

  // Watch the streamed message list and forward each new tool call's patch.
  const seenRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const patches = extractDraftPatches(chat.messages, seenRef.current);
    for (const p of patches) {
      seenRef.current.add(p.id);
      onDraftPatch(p.field, p.value);
    }
  }, [chat.messages, onDraftPatch]);

  return {
    runtime,
    chatMessages: chat.messages,
    setChatMessages: chat.setMessages,
    status: chat.status,
    error: chat.error,
  };
}
